import {
  startAuthentication,
  startRegistration,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { config } from '../platform/index.ts';
import { sessionVerdict } from '../platform/index.ts';

// The accounts service's browser surface: password sign-in, the rotating refresh
// (an HttpOnly cookie scoped to /accounts/v1, never readable here) and logout.
// The access token it returns lives in memory only (see api.ts).

const base = new URL(config.VITE_ACCOUNTS_BASE_URL, window.location.origin).toString().replace(/\/$/, '');

export interface Session {
  accessToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
}

export class SignInError extends Error {
  readonly status: number;
  /** The service's error code (`unverified`: the address is not confirmed yet), when it sent one. */
  readonly code: string | undefined;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = 'SignInError';
  }
}

const post = (path: string, body?: unknown) =>
  fetch(`${base}/accounts/v1${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

// A hint (not a secret) that this browser holds a refresh cookie, so a visitor who never signed in
// is not sent to /refresh on every load. The cookie itself stays HttpOnly.
const HINT_KEY = 'cortex_session_hint';
const hint = {
  get: () => {
    try {
      return localStorage.getItem(HINT_KEY) === '1';
    } catch {
      return true;
    }
  },
  set: (on: boolean) => {
    try {
      if (on) localStorage.setItem(HINT_KEY, '1');
      else localStorage.removeItem(HINT_KEY);
    } catch {
      /* the refresh is then always tried */
    }
  },
};

const session = (j: { access_token: string; expires_in: number }): Session => {
  hint.set(true);
  return { accessToken: j.access_token, expiresIn: j.expires_in };
};

/**
 * A right password still waiting for its second factor (ADR 0014 §9). The token lives only in memory
 * (the sign-in flow's router state), never in a URL or storage.
 */
export interface MfaChallenge {
  mfaToken: string;
  /** The organization's policy requires two-factor and the account has none: set it up first. */
  enrol: boolean;
  expiresIn: number;
  /** The second factors the account has (threat model T3: an authenticator app, security keys). */
  factors: { totp: boolean; securityKey: boolean };
  /** An organization accepts only a security key (phishing-resistant policy): no codes. */
  securityKeyRequired: boolean;
}

export const isMfaChallenge = (v: Session | MfaChallenge): v is MfaChallenge => 'mfaToken' in v;

export const WRONG_CREDENTIALS = 'Email or password is incorrect.';

/** The second-factor step a right password still owes, from the service's 202 body. */
function challengeOf(j: {
  mfa_token: string;
  enrol: boolean;
  expires_in: number;
  factors?: { totp?: boolean; security_key?: boolean };
  security_key_required?: boolean;
}): MfaChallenge {
  return {
    mfaToken: j.mfa_token,
    enrol: j.enrol,
    expiresIn: j.expires_in,
    factors: { totp: j.factors?.totp ?? !j.enrol, securityKey: j.factors?.security_key ?? false },
    securityKeyRequired: j.security_key_required ?? false,
  };
}

/** Signs in with email and password: a session, or the second factor to ask for. Throws SignInError. */
export async function signIn(email: string, password: string): Promise<Session | MfaChallenge> {
  let res: Response;
  try {
    res = await post('/login', { email, password });
  } catch {
    throw new SignInError('The sign-in service is unreachable.', 0);
  }
  if (res.status === 202) return challengeOf(await res.json());
  if (res.ok) return session(await res.json());
  // One answer for an unknown email and a wrong password: no account enumeration.
  if (res.status === 401) throw new SignInError(WRONG_CREDENTIALS, 401);
  const j = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
  // The rate limit (and how long to wait) as the service words it.
  if (res.status === 429)
    throw new SignInError(j.message ?? 'Too many sign-in attempts. Wait a minute and try again.', 429);
  // Password sign-in refused by policy (the organization enforces SSO, or passwords are off): say why.
  if (res.status === 403 || res.status === 404)
    throw new SignInError(
      j.message ?? 'Sign in with your organization’s single sign-on.',
      res.status,
      j.error,
    );
  throw new SignInError(`Sign-in failed (${res.status}).`, res.status);
}

// ── Two-factor authentication (ADR 0014 §9) ───────────────────────────────────────────────────────

/** An authenticator being set up: the seed (the manual key) and the otpauth URI the QR code carries. */
export interface MfaEnrolment {
  secret: string;
  uri: string;
  account: string;
  issuer: string;
}

export interface MfaStatus {
  enabled: boolean;
  enabledAt: string | null;
  pending: boolean;
  recoveryCodesLeft: number;
  lockedUntil: string | null;
  requiredBy: string[];
  securityKeys: SecurityKey[];
  /** The organizations that accept only a security key for this person's password sign-ins. */
  securityKeyRequiredBy: string[];
}

/** A security key (WebAuthn credential) of the signed-in person (threat model T3). */
export interface SecurityKey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  backedUp: boolean;
}

/** The proof before a change to the second factors: a current authenticator code, or a security key. */
export type StepUp = { code: string } | { securityKey: AuthenticationResponseJSON };

export interface MfaPolicy {
  org: string;
  requirePasswordMfa: boolean;
  requirePhishingResistant: boolean;
  updatedAt: string | null;
}

/** The error of a two-factor call: its message for the person, and the service's reason code. */
export class MfaError extends Error {
  readonly status: number;
  readonly reason?: string;
  constructor(message: string, status: number, reason?: string) {
    super(message);
    this.status = status;
    this.reason = reason;
    this.name = 'MfaError';
  }
}

async function mfaCall<T>(method: string, path: string, body?: unknown, token?: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/v1${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new MfaError('The sign-in service is unreachable.', 0);
  }
  const j = (await res.json().catch(() => ({}))) as { message?: string; reason?: string } & T;
  if (!res.ok)
    throw new MfaError(j.message ?? `The sign-in service answered ${res.status}.`, res.status, j.reason);
  return j;
}

type TokenJson = { access_token: string; expires_in: number };

/** The held sign-in's second factor: an authenticator code or a recovery code. */
export async function verifyMfa(mfaToken: string, code: string): Promise<Session> {
  return session(await mfaCall<TokenJson>('POST', '/mfa/verify', { mfa_token: mfaToken, code }));
}

export const mfa = {
  status: (token: string) => mfaCall<MfaStatus>('GET', '/mfa', undefined, token),
  /** Starts a set-up, for the signed-in person (token) or the held sign-in (mfaToken). */
  enrol: (auth: { token?: string; mfaToken?: string }) =>
    mfaCall<MfaEnrolment>(
      'POST',
      '/mfa/enrol',
      auth.mfaToken ? { mfa_token: auth.mfaToken } : {},
      auth.token,
    ),
  /** Turns it on with a first code (a person with a security key proves it too); a held sign-in also gets its session. */
  async confirm(auth: { token?: string; mfaToken?: string }, code: string, stepUp?: StepUp) {
    const j = await mfaCall<{ recovery_codes: string[]; session?: TokenJson }>(
      'POST',
      '/mfa/enrol/confirm',
      { code, ...(auth.mfaToken ? { mfa_token: auth.mfaToken } : {}), ...(stepUp ? { stepUp } : {}) },
      auth.token,
    );
    return { recoveryCodes: j.recovery_codes, session: j.session ? session(j.session) : null };
  },
  /** New recovery codes, proven by a current authenticator code or a security key. */
  regenerate: async (token: string, proof: StepUp) =>
    (
      await mfaCall<{ recovery_codes: string[] }>(
        'POST',
        '/mfa/recovery-codes',
        'code' in proof ? { code: proof.code } : { stepUp: proof },
        token,
      )
    ).recovery_codes,
  disable: (token: string, code: string) => mfaCall<{ disabled: boolean }>('DELETE', '/mfa', { code }, token),
  policy: (org: string, token: string) =>
    mfaCall<MfaPolicy>('GET', `/orgs/${encodeURIComponent(org)}/mfa-policy`, undefined, token),
  setPolicy: (
    org: string,
    p: { requirePasswordMfa: boolean; requirePhishingResistant?: boolean },
    token: string,
  ) => mfaCall<MfaPolicy>('PUT', `/orgs/${encodeURIComponent(org)}/mfa-policy`, p, token),
  reset: (org: string, email: string, token: string) =>
    mfaCall<{ reset: boolean; hadMfa: boolean; sessionsEnded: number }>(
      'POST',
      `/orgs/${encodeURIComponent(org)}/mfa-reset`,
      { email },
      token,
    ),
};

// ── Security keys (WebAuthn, threat model T3) ─────────────────────────────────────────────────────

/** The browser's own security-key prompt; a cancel or timeout is an MfaError in plain words. */
async function ceremony<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof MfaError) throw e;
    const name = e instanceof Error ? e.name : '';
    throw new MfaError(
      name === 'NotAllowedError' || name === 'AbortError'
        ? 'The security key request was cancelled or timed out.'
        : name === 'InvalidStateError'
          ? 'This security key is already registered.'
          : 'This browser could not use a security key.',
      0,
      'browser',
    );
  }
}

/** The held sign-in's second factor: a security key. */
export async function verifySecurityKey(mfaToken: string): Promise<Session> {
  const optionsJSON = await mfaCall<PublicKeyCredentialRequestOptionsJSON>(
    'POST',
    '/mfa/security-keys/verify/options',
    { mfa_token: mfaToken },
  );
  const credential = await ceremony(() => startAuthentication({ optionsJSON }));
  return session(
    await mfaCall<TokenJson>('POST', '/mfa/security-keys/verify', { mfa_token: mfaToken, credential }),
  );
}

export const securityKeys = {
  /**
   * Adds a security key, for the signed-in person (token; `stepUp` when they have a second factor) or
   * the held sign-in (mfaToken, which then gets its session). Recovery codes when it is the first factor.
   */
  async add(auth: { token?: string; mfaToken?: string }, name: string, stepUp?: StepUp) {
    const held = auth.mfaToken ? { mfa_token: auth.mfaToken } : {};
    const optionsJSON = await mfaCall<PublicKeyCredentialCreationOptionsJSON>(
      'POST',
      '/mfa/security-keys/register/options',
      held,
      auth.token,
    );
    const credential = await ceremony(() => startRegistration({ optionsJSON }));
    const j = await mfaCall<{ key: SecurityKey; recovery_codes?: string[]; session?: TokenJson }>(
      'POST',
      '/mfa/security-keys',
      { ...held, name, credential, ...(stepUp ? { stepUp } : {}) },
      auth.token,
    );
    return {
      key: j.key,
      recoveryCodes: j.recovery_codes ?? null,
      session: j.session ? session(j.session) : null,
    };
  },
  /** A step-up proof by a security key of the signed-in person other than `exclude`. */
  async stepUp(token: string, exclude?: string): Promise<StepUp> {
    const optionsJSON = await mfaCall<PublicKeyCredentialRequestOptionsJSON>(
      'POST',
      '/mfa/security-keys/step-up/options',
      exclude ? { exclude } : {},
      token,
    );
    return { securityKey: await ceremony(() => startAuthentication({ optionsJSON })) };
  },
  rename: (token: string, id: string, name: string) =>
    mfaCall<{ renamed: boolean }>('PATCH', `/mfa/security-keys/${encodeURIComponent(id)}`, { name }, token),
  remove: (token: string, id: string, stepUp: StepUp) =>
    mfaCall<{ removed: boolean }>(
      'DELETE',
      `/mfa/security-keys/${encodeURIComponent(id)}`,
      { stepUp },
      token,
    ),
};

/**
 * A tool the person signed in to cortex's MCP with (ADR 0030; the accounts service's grants of an MCP client):
 * what it reaches (`project`: one project, or '*' for every project the person can open) and with which scopes.
 */
export interface ConnectedTool {
  id: string;
  kind: string;
  client_id: string;
  client_name: string | null;
  project: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** The `project` of a sign-in that reaches every project the person can open (the accounts service's wire value). */
export const EVERY_PROJECT = '*';

export const connectedTools = {
  /** The person's live MCP sign-ins, newest first. */
  async list(token: string): Promise<ConnectedTool[]> {
    const r = await mfaCall<{ grants: ConnectedTool[] }>('GET', '/oauth/grants', undefined, token);
    return r.grants.filter((g) => g.kind === 'authorization_code' && !g.revoked_at);
  },
  /** Ends a sign-in: every token it issued, and every project token exchanged from it, stops at once. */
  disconnect: (token: string, id: string) =>
    mfaCall<{ revoked: boolean }>('DELETE', `/oauth/grants/${encodeURIComponent(id)}`, undefined, token),
};

/** The accounts service could not answer the refresh (down, restarting): the session may well be live. */
export const UNAVAILABLE = 'unavailable' as const;

/**
 * A fresh access token from the refresh cookie; null when there is no live session (no cookie hint, or the
 * service said so); UNAVAILABLE when the service could not answer (no answer, a 5xx), which ends nothing:
 * the caller retries (Board #2649).
 */
export async function refreshSession(): Promise<Session | null | typeof UNAVAILABLE> {
  if (!hint.get()) return null;
  let res: Response;
  try {
    res = await post('/refresh');
  } catch {
    return UNAVAILABLE;
  }
  const verdict = sessionVerdict(res.status);
  if (verdict === 'unavailable') return UNAVAILABLE;
  if (verdict === 'signed-in') {
    try {
      return session(await res.json());
    } catch {
      return UNAVAILABLE;
    }
  }
  if (res.status === 401) hint.set(false);
  return null;
}

/** Ends the session (revokes the refresh token and clears its cookie). Never throws. */
export async function signOut(): Promise<void> {
  hint.set(false);
  try {
    await post('/logout');
  } catch {
    /* signed out locally either way */
  }
}

// ── Single sign-on (ADR 0014) ─────────────────────────────────────────────────────────────────────

/** The identity provider behind a connection, for its icon (the accounts service reads it off the issuer). */
export type SsoProvider = 'google' | 'microsoft' | 'okta' | 'oidc';

export interface SsoConnection {
  id: string;
  label: string;
  start: string;
  provider: SsoProvider;
}

const PROVIDERS: readonly SsoProvider[] = ['google', 'microsoft', 'okta', 'oidc'];

/** A connection as the service sent it; an unknown or missing provider is a generic one. */
const connectionOf = (c: Partial<SsoConnection>): SsoConnection | null =>
  typeof c.id === 'string' && typeof c.label === 'string' && typeof c.start === 'string'
    ? {
        id: c.id,
        label: c.label,
        start: c.start,
        provider: PROVIDERS.includes(c.provider as SsoProvider) ? (c.provider as SsoProvider) : 'oidc',
      }
    : null;

export interface SignInOptions {
  connections: SsoConnection[];
  /** on, break_glass: the password form (the service decides whose password it takes); off: SSO only. */
  password: 'on' | 'break_glass' | 'off';
}

/** The sign-in options of this installation; password only when the accounts service is unreachable. */
export async function signInOptions(): Promise<SignInOptions> {
  try {
    const res = await fetch(`${base}/accounts/v1/sso/status`, { credentials: 'include' });
    if (!res.ok) return { connections: [], password: 'on' };
    const j = (await res.json()) as Partial<SignInOptions>;
    return {
      connections: Array.isArray(j.connections) ? j.connections.flatMap((c) => connectionOf(c) ?? []) : [],
      password: j.password === 'break_glass' || j.password === 'off' ? j.password : 'on',
    };
  } catch {
    return { connections: [], password: 'on' };
  }
}

/**
 * The organization single sign-on of an email address (by its domain), or null. The sign-in page asks
 * it for a connection that is not shown as a button (show on sign-in off), so its people still find it.
 */
export async function discoverSso(email: string): Promise<SsoConnection | null> {
  try {
    const res = await post('/sso/discover', { email });
    if (!res.ok) return null;
    const c = ((await res.json()) as { connection: Partial<SsoConnection> | null }).connection;
    return c ? connectionOf(c) : null;
  } catch {
    return null;
  }
}

/** Leaves the app for the identity provider; the session comes back to `returnUrl`. */
export function startSso(c: SsoConnection, returnUrl: string) {
  const u = new URL(`${base}${c.start}`);
  u.searchParams.set('return', returnUrl);
  // The provider's callback sets the refresh cookie; the app back from it must try /refresh (a failed
  // sign-in clears the hint on that refresh's 401).
  hint.set(true);
  window.location.assign(u.toString());
}

/**
 * Where a sign-in may continue after it succeeds: this app only. Anything else is ignored (no open redirect).
 */
export function safeReturn(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin === window.location.origin && !u.pathname.startsWith('/accounts/')) return u.toString();
  } catch {
    /* not a URL */
  }
  return null;
}

const SSO_REASONS: Record<string, string> = {
  unknown_state: 'This sign-in expired or was not started here. Sign in again.',
  unknown_connection: 'This single sign-on is not configured.',
  provider_error: 'The identity provider refused the sign-in.',
  no_email: 'The identity provider did not return an email address.',
  unverified_email: 'The identity provider has not verified your email address.',
  domain: 'Your email domain is not allowed to sign in here.',
  unknown_account: 'There is no account for your email yet. Ask your administrator to give you access.',
  disabled: 'Your account is disabled.',
  managed_elsewhere: 'Your account belongs to another organization. Use its sign-in button.',
  provisioning: 'Your organization’s roles could not be applied. Try again in a minute.',
  id_token: 'The identity provider’s answer could not be verified.',
  nonce: 'The identity provider’s answer could not be verified.',
  exchange: 'The identity provider did not complete the sign-in.',
  discovery: 'The identity provider is unavailable.',
};

export const ssoErrorText = (reason: string) =>
  SSO_REASONS[reason] ?? 'Single sign-on could not be completed. Try again.';

// ── An organization's identity administration (SSO connection, SCIM token) ─────────────────────────

export type OrgRole = 'org_admin' | 'member' | 'app_user';

export interface GroupMapping {
  group: string;
  role?: OrgRole;
  project?: string;
  projectRole?: 'viewer' | 'editor';
}

/**
 * A listed email domain: claimed, and proven once the organization published `record` in its DNS (or a
 * platform admin approved it). Only a proven domain lets the identity provider reach accounts by email.
 */
export interface OrgDomain {
  domain: string;
  proven: boolean;
  provenAt: string | null;
  provenBy: 'dns' | 'admin' | null;
  record: { name: string; value: string };
}

export interface OrgConnection {
  org: string;
  label: string;
  issuer: string;
  clientId: string;
  hasClientSecret: boolean;
  scopes: string;
  allowedDomains: string[];
  domains: OrgDomain[];
  groupClaim: string;
  groupMappings: GroupMapping[];
  defaultRole: OrgRole | null;
  jitProvisioning: boolean;
  enforceSso: boolean;
  showOnLogin: boolean;
  enabled: boolean;
  updatedAt: string;
}

export interface OrgIdentity {
  org: string;
  callbackUrl: string;
  connection: OrgConnection | null;
  scim: {
    baseUrl: string;
    token: { prefix: string; createdAt: string; expiresAt: string; lastUsedAt: string | null } | null;
    users: number;
    groups: number;
  };
  secretsConfigured: boolean;
}

export type ConnectionInput = Omit<
  OrgConnection,
  'org' | 'hasClientSecret' | 'updatedAt' | 'scopes' | 'groupClaim' | 'domains'
> & {
  clientSecret?: string | null;
  scopes?: string;
  groupClaim?: string;
};

export class AccountsError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'AccountsError';
  }
}

async function admin<T>(method: string, path: string, token: string | undefined, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/v1${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new AccountsError('The sign-in service is unreachable.', 0);
  }
  const j = (await res.json().catch(() => ({}))) as { message?: string } & T;
  if (!res.ok)
    throw new AccountsError(j.message ?? `The sign-in service answered ${res.status}.`, res.status);
  return j;
}

export const orgIdentity = {
  get: (org: string, token?: string) =>
    admin<OrgIdentity>('GET', `/orgs/${encodeURIComponent(org)}/identity`, token),
  putSso: (org: string, c: ConnectionInput, token?: string) =>
    admin<OrgIdentity>('PUT', `/orgs/${encodeURIComponent(org)}/identity/sso`, token, c),
  deleteSso: (org: string, token?: string) =>
    admin<OrgIdentity>('DELETE', `/orgs/${encodeURIComponent(org)}/identity/sso`, token),
  issueScimToken: (org: string, token?: string) =>
    admin<{ token: string; prefix: string; expiresAt: string; baseUrl: string }>(
      'POST',
      `/orgs/${encodeURIComponent(org)}/identity/scim-token`,
      token,
    ),
  revokeScimToken: (org: string, token?: string) =>
    admin<OrgIdentity>('DELETE', `/orgs/${encodeURIComponent(org)}/identity/scim-token`, token),
  /** Checks the domain's DNS record; `approve` is a platform admin's approval without it. */
  verifyDomain: (org: string, domain: string, token?: string, approve?: boolean) =>
    admin<OrgIdentity>('POST', `/orgs/${encodeURIComponent(org)}/identity/domains/verify`, token, {
      domain,
      ...(approve ? { approve: true } : {}),
    }),
};

// ── Password reset and sign-up by invitation (ADR 0014 §10) ───────────────────────────────────────

async function accountsCall<T>(path: string, body: unknown, token?: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/v1${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AccountsError('The sign-in service is unreachable.', 0);
  }
  const j = (await res.json().catch(() => ({}))) as { message?: string } & T;
  if (res.status === 429) throw new AccountsError('Too many attempts. Wait a minute and try again.', 429);
  if (!res.ok)
    throw new AccountsError(j.message ?? `The sign-in service answered ${res.status}.`, res.status);
  return j;
}

/** Asks for a reset link; the answer is the same whether or not the address has an account. */
export const requestPasswordReset = (email: string) =>
  accountsCall<{ accepted: boolean }>('/password/forgot', { email });

/** Sets a new password with a reset link's token; every session of the account ends. */
export const resetPassword = (token: string, password: string) =>
  accountsCall<{ reset: boolean }>('/password/reset', { token, password });

/**
 * What an invitation link is for: joining an organization (`org`: org, orgName, role; ADR 0014 §10), or opening
 * one app that is open to anyone signed in (`app`: project, app, appTitle; ADR 0044 §4).
 */
export interface InvitationLookup {
  kind: 'org' | 'app';
  id: string;
  email: string;
  org?: string;
  orgName?: string;
  role?: string;
  project?: string;
  app?: string;
  appTitle?: string;
  expiresAt: string;
  hasAccount: boolean;
  signup: boolean;
  ssoRequired: boolean;
}

/** What an invitation link is for. Throws AccountsError 404 for one that is not valid any more. */
export const lookupInvitation = (token: string) =>
  accountsCall<InvitationLookup>('/invitations/lookup', { token });

/**
 * Creates the invited account with a password and signs in. `redirect`: where the new account lands — an app
 * invitation's app address on the runtime, or, for an app user's organization invitation riding on app shares, the
 * share link of the app (a path of this web); null: cortex itself.
 */
export async function signupByInvitation(b: {
  token: string;
  password: string;
  first_name?: string;
  last_name?: string;
}): Promise<{ session: Session; redirect: string | null }> {
  const j = await accountsCall<TokenJson & { redirect?: string }>('/invitations/accept', b);
  return { session: session(j), redirect: j.redirect ?? null };
}

// ── Self sign-up with email verification (ADR 0044 §3) ────────────────────────────────────────────

export interface SignupOptions {
  enabled: boolean;
  termsUrl: string | null;
}

/** Whether self sign-up is open here and the terms it links to; closed when the service does not answer. */
export async function signupOptions(): Promise<SignupOptions> {
  try {
    const res = await fetch(`${base}/accounts/v1/signup/options`, { credentials: 'include' });
    if (!res.ok) return { enabled: false, termsUrl: null };
    const j = (await res.json()) as Partial<SignupOptions>;
    return { enabled: j.enabled === true, termsUrl: typeof j.termsUrl === 'string' ? j.termsUrl : null };
  } catch {
    return { enabled: false, termsUrl: null };
  }
}

/** Creates an account and mails it a confirmation link. There is no session until the link is used. */
export const signUp = (b: {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  accept_terms?: boolean;
}) => accountsCall<{ status: 'verify_email' }>('/signup', b);

/**
 * Uses a confirmation link's token (once) with the password chosen at sign-up. A session, or the second-factor
 * step a password sign-in would owe (202). Throws AccountsError for a wrong password, a dead link, or an
 * organization that requires single sign-on (403).
 */
export async function verifySignup(token: string, password: string): Promise<Session | MfaChallenge> {
  let res: Response;
  try {
    res = await post('/signup/verify', { token, password });
  } catch {
    throw new AccountsError('The sign-in service is unreachable.', 0);
  }
  const j = (await res.json().catch(() => ({}))) as Record<string, unknown> & { message?: string };
  if (res.status === 202) return challengeOf(j as Parameters<typeof challengeOf>[0]);
  if (res.status === 201 || res.ok) return session(j as unknown as TokenJson);
  if (res.status === 429) throw new AccountsError('Too many attempts. Wait a minute and try again.', 429);
  throw new AccountsError(j.message ?? `The sign-in service answered ${res.status}.`, res.status);
}

/** Asks for a new confirmation link; the answer is the same for any address. */
export const resendSignup = (email: string) =>
  accountsCall<{ accepted: boolean }>('/signup/resend', { email });

/** Platform admin: mails a member a reset link, or returns it when no mail could be sent. */
export const sendPasswordResetLink = (org: string, email: string, token: string) =>
  accountsCall<{ sent: boolean; expiresAt: string; resetUrl?: string }>(
    `/orgs/${encodeURIComponent(org)}/people/password-reset`,
    { email },
    token,
  );

/** The fragment token of a link (`#token=…`), or ''. */
export const fragmentToken = (hash: string) => new URLSearchParams(hash.replace(/^#/, '')).get('token') ?? '';
